<template>
    <RegularModal ref="modal" modal-card-class="bracket-slicing-modal" @closed="emit('closed')">
        <template v-slot:title>
            <span>{{ translate("admin_schedule_manage.round_slicing_title") }}</span>
        </template>
        <template v-slot:body>
            <div v-if="currentBracket">
                <p class="has-text-weight-bold mb-2">{{ currentBracket.name }}</p>

                <div v-if="currentBracket.supportsRoundSlicing" class="field">
                    <label class="label">{{ translate("admin_schedule_manage.rounds") }}</label>
                    <div class="round-chip-list">
                        <button
                            v-for="round in availableRounds"
                            :key="round"
                            class="button is-small"
                            :class="{ 'is-info': isRoundIsolated(round), 'is-light': !isRoundIsolated(round) }"
                            @click="toggleRound(round)"
                        >
                            {{ formatRoundMarker(round) }}
                        </button>
                    </div>
                </div>

                <div v-if="selectedRounds.length > 0" class="field">
                    <label class="label">{{ translate("admin_schedule_manage.parts") }}</label>
                    <div v-for="round in selectedRounds" :key="round" class="slicing-round-row">
                        <span>{{ formatRoundMarker(round) }}</span>
                        <div class="buttons are-small has-addons mb-0">
                            <button
                                v-for="parts in getAvailableEqualPartsForRound(round)"
                                :key="parts"
                                class="button"
                                :class="{ 'is-info': equalPartsFor(round) === parts }"
                                @click="setEqualParts(round, parts)"
                            >
                                {{ parts }}
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else class="field">
                    <label class="label">{{ translate("admin_schedule_manage.parts") }}</label>
                    <div class="slicing-round-row">
                        <span>{{ translate("admin_schedule_manage.whole_bracket") }}</span>
                        <div class="buttons are-small has-addons mb-0">
                            <button
                                v-for="parts in availableEqualParts"
                                :key="parts"
                                class="button"
                                :class="{ 'is-info': wholeBracketEqualParts === parts }"
                                @click="setWholeBracketEqualParts(parts)"
                            >
                                {{ parts }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="field">
                    <label class="label">{{ translate("admin_schedule_manage.preview") }}</label>
                    <div class="slicing-preview">
                        <div v-for="item in previewItems" :key="item.key" class="slicing-preview-item">
                            <span v-if="item.segment.from === null && item.segment.to === null && item.assignmentType === 1">
                                {{ translate("admin_schedule_manage.whole_bracket") }}
                            </span>
                            <ScheduleSegmentTag
                                v-else
                                :from="item.segment.from"
                                :to="item.segment.to"
                                :includes-third-place="item.segment.includesThirdPlace"
                                :assignment-type="item.assignmentType"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="saving" class="button is-success" @click="save">
                    <span class="icon"><i class="fas fa-check"></i></span>
                    <span>{{ translate("common.save") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { useModal } from "../../Common/useModal";
    import { translate } from "../../Common/translations";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import schedulesApi from "../../Api/schedules";
    import type { BracketSchedulingConfig } from "../../Common/Brackets/types";
    import {
        BracketSlicingTarget,
        RoundMarker,
        WHOLE_BRACKET_EQUAL_PARTS_KEY,
        expandBracketToDropZoneItems,
        getAvailableEqualPartsForRound,
        getAvailableEqualPartsForWholeBracket,
        getAvailableRounds,
        getRoundMarkerTranslationKey,
    } from "./dropZoneItems";
    import ScheduleSegmentTag from "../ScheduleSegmentTag.vue";
    import { useScheduleMainStore } from "./useScheduleMainStore";

    const emit = defineEmits<{
        configSaved: [];
        closed: [];
    }>();

    const mainStore = useScheduleMainStore();
    const { modal, open: openModal, close } = useModal();
    const currentBracket = ref<BracketSlicingTarget | null>(null);
    const isolatedRounds = ref<RoundMarker[]>([]);
    const equalParts = ref<Record<string, number>>({});
    const saving = ref(false);
    const availableEqualParts = computed(() => getAvailableEqualPartsForWholeBracket(currentBracket.value?.total_real_competitors ?? 0));

    const availableRounds = computed(() => {
        if (currentBracket.value === null) {
            return [];
        }

        return getAvailableRounds(currentBracket.value.total_real_competitors, currentBracket.value.bracketType);
    });

    const selectedRounds = computed(() => {
        return availableRounds.value.filter(round => isolatedRounds.value.includes(round));
    });

    const wholeBracketEqualParts = computed((): 1 | 2 | 4 => {
        const configured = equalParts.value[WHOLE_BRACKET_EQUAL_PARTS_KEY];

        if (configured === 2 || configured === 4) {
            return configured;
        }

        return 1;
    });

    const previewItems = computed(() => {
        if (currentBracket.value === null) {
            return [];
        }

        return expandBracketToDropZoneItems({
            ...currentBracket.value,
            schedulingConfig: buildConfig(),
        });
    });

    function open(complexBracket: BracketSlicingTarget) {
        currentBracket.value = complexBracket;
        isolatedRounds.value = (complexBracket.schedulingConfig?.isolatedRounds ?? []) as RoundMarker[];
        const loaded = { ...(complexBracket.schedulingConfig?.equalParts ?? {}) };
        for (const round of isolatedRounds.value) {
            const allowed = getAvailableEqualPartsForRound(round);
            if (round in loaded && !allowed.includes(loaded[round] as 1 | 2 | 4)) {
                loaded[round] = 1;
            }
        }
        const allowedWholeBracketParts = getAvailableEqualPartsForWholeBracket(complexBracket.total_real_competitors);
        if (
            WHOLE_BRACKET_EQUAL_PARTS_KEY in loaded &&
            !allowedWholeBracketParts.includes(loaded[WHOLE_BRACKET_EQUAL_PARTS_KEY] as 1 | 2 | 4)
        ) {
            loaded[WHOLE_BRACKET_EQUAL_PARTS_KEY] = 1;
        }
        equalParts.value = loaded;
        openModal();
    }

    function isRoundIsolated(round: RoundMarker): boolean {
        return isolatedRounds.value.includes(round);
    }

    function toggleRound(round: RoundMarker) {
        if (isRoundIsolated(round)) {
            isolatedRounds.value = isolatedRounds.value.filter(selectedRound => selectedRound !== round);
            const nextEqualParts = { ...equalParts.value };
            delete nextEqualParts[round];
            equalParts.value = nextEqualParts;
            return;
        }

        isolatedRounds.value = [...isolatedRounds.value, round];
        const nextEqualParts = { ...equalParts.value };
        delete nextEqualParts[WHOLE_BRACKET_EQUAL_PARTS_KEY];
        equalParts.value = nextEqualParts;
    }

    function equalPartsFor(round: RoundMarker): 1 | 2 | 4 {
        const configured = equalParts.value[round];

        if (configured === 2 || configured === 4) {
            return configured;
        }

        return 1;
    }

    function setEqualParts(round: RoundMarker, parts: 1 | 2 | 4) {
        equalParts.value = {
            ...equalParts.value,
            [round]: parts,
        };
    }

    function setWholeBracketEqualParts(parts: 1 | 2 | 4) {
        equalParts.value = {
            ...equalParts.value,
            [WHOLE_BRACKET_EQUAL_PARTS_KEY]: parts,
        };
    }

    function save() {
        if (currentBracket.value === null) {
            return;
        }

        saving.value = true;
        mainStore
            .trackRequest(schedulesApi.saveBracketSchedulingConfig(mainStore.eventId, currentBracket.value.parameter_id, buildConfig()))
            .then(() => {
                successToast(translate("admin_schedule_manage.slicing_saved"));
                emit("configSaved");
                close();
            })
            .catch(handleError)
            .finally(() => {
                saving.value = false;
            });
    }

    function buildConfig(): BracketSchedulingConfig {
        const selected = selectedRounds.value;
        const selectedSet = new Set(selected);
        const selectedEqualParts: Record<string, number> = {};

        // equalParts=1 is the default and is never persisted — only values of 2 or 4 are meaningful to save.
        // On load, any missing key is treated as 1 by the resolver, so omitting it is equivalent to storing 1.
        Object.entries(equalParts.value).forEach(([round, parts]) => {
            if (selectedSet.has(round as RoundMarker) && (parts === 2 || parts === 4)) {
                selectedEqualParts[round] = parts;
            }
        });

        if (selected.length === 0 && (wholeBracketEqualParts.value === 2 || wholeBracketEqualParts.value === 4)) {
            selectedEqualParts[WHOLE_BRACKET_EQUAL_PARTS_KEY] = wholeBracketEqualParts.value;
        }

        return {
            isolatedRounds: selected,
            equalParts: selectedEqualParts,
        };
    }

    function formatRoundMarker(marker: RoundMarker | null): string {
        if (marker === null) {
            return "";
        }

        return translate(getRoundMarkerTranslationKey(marker)) ?? marker;
    }

    defineExpose({ open });
</script>

<style scoped>
    .round-chip-list {
        display: flex;
        flex-wrap: wrap;
        gap: 0.35rem;
    }

    .slicing-round-row,
    .slicing-preview-item {
        align-items: center;
        display: flex;
        justify-content: space-between;
        gap: 0.75rem;
        padding: 0.45rem 0;
    }

    .slicing-round-row + .slicing-round-row,
    .slicing-preview-item + .slicing-preview-item {
        border-top: 1px solid rgba(255, 255, 255, 0.08);
    }
</style>
